/**
 * cajaResumenVentasSesion.ts — Resumen de ventas por sesión (cobrado, neto, propina).
 *
 * Usado por mocks GET /caja/estado, POST cerrar y UI de cierre. Una sola fuente
 * evita que el modal, la gaveta esperada y el ticket muestren cifras distintas.
 *
 * NO debe: importar React ni leer localStorage.
 */

import type { MovimientoCaja, Venta } from "../types/caja.types";
import type { PaymentMethodsConfig } from "@/lib/paymentMethodsConfig";
import {
  buildResumenSlotRows,
  extractPaymentLinesFromVenta,
  isDigitalIngresoSlot,
  slotToLegacyBucket,
  type ResumenSlotRow,
} from "./paymentMethodAggregation";
import {
  sumPropinaPorMetodo,
  sumSubtotalPorMetodo,
  totalPropinaMontos,
  ventasActivasSesion,
} from "./propinaAllocation";
import { isMovimientoAbonoCredito } from "./movimientoAbonoCredito";
import { splitEgresosCajaFisica } from "./splitEgresosCajaFisica";
import { calcEsperadoGaveta } from "./cierreCuadranteHelper";

export type { ResumenSlotRow };

export interface ResumenVentasSesion {
  /** Filas dinámicas por slot inmutable (fuente de verdad para UI). */
  porSlot: ResumenSlotRow[];
  /** Buckets legacy para APIs / compatibilidad. */
  efectivo: number;
  nequi: number;
  daviplata: number;
  tarjeta: number;
  otros: number;
  /** Total cobrado (neto + propina) en todos los métodos. */
  total: number;
  /** Ventas netas (sin propina), todos los métodos. */
  subtotalTotal: number;
  /** Propinas cobradas en la sesión. */
  propinaTotal: number;
  ventasCount: number;
  count_efectivo: number;
  count_nequi: number;
  count_daviplata: number;
  count_tarjeta: number;
  count_otros: number;
}

function countTransaccionesPorMetodo(
  ventas: Venta[],
): Pick<
  ResumenVentasSesion,
  | "count_efectivo"
  | "count_nequi"
  | "count_daviplata"
  | "count_tarjeta"
  | "count_otros"
> {
  const counts = {
    count_efectivo: 0,
    count_nequi: 0,
    count_daviplata: 0,
    count_tarjeta: 0,
    count_otros: 0,
  };

  ventas.forEach((v) => {
    const lines = extractPaymentLinesFromVenta(v);
    if (lines.length === 0) {
      counts.count_otros += 1;
      return;
    }
    lines.forEach((line) => {
      const bucket = slotToLegacyBucket(line.slotId);
      if (bucket === "otros") counts.count_otros += 1;
      else (counts as Record<string, number>)[`count_${bucket}`] += 1;
    });
  });

  return counts;
}

function legacyBucketsFromPorSlot(
  porSlot: ResumenSlotRow[],
): Pick<
  ResumenVentasSesion,
  "efectivo" | "nequi" | "daviplata" | "tarjeta" | "otros"
> {
  const out = { efectivo: 0, nequi: 0, daviplata: 0, tarjeta: 0, otros: 0 };
  porSlot.forEach((row) => {
    const bucket = slotToLegacyBucket(row.slotId);
    if (bucket === "otros") out.otros += row.total;
    else out[bucket] += row.total;
  });
  return out;
}

/** Efectivo físico en gaveta por ventas (cobrado en efectivo, incluye propina en efectivo). */
export function totalEfectivoCobradoVentas(ventas: Venta[]): number {
  const porSlot = buildResumenSlotRows(ventas);
  return porSlot.find((r) => r.slotId === "efectivo")?.total ?? 0;
}

/**
 * Agrega ventas de una sesión: montos cobrados por método + neto/propina + conteos.
 * Excluye ventas anuladas.
 */
export function calcResumenVentasSesion(
  ventas: Venta[],
  sesionId: string,
  cfg?: PaymentMethodsConfig | null,
): ResumenVentasSesion {
  const activas = ventasActivasSesion(ventas, sesionId);
  const porSlot = buildResumenSlotRows(activas, cfg);
  const legacy = legacyBucketsFromPorSlot(porSlot);
  const propina = sumPropinaPorMetodo(activas);
  const counts = countTransaccionesPorMetodo(activas);
  const total = porSlot.reduce((s, r) => s + r.total, 0);

  return {
    porSlot,
    ...legacy,
    total,
    subtotalTotal: Math.max(0, total - totalPropinaMontos(propina)),
    propinaTotal: totalPropinaMontos(propina),
    ventasCount: activas.length,
    ...counts,
  };
}

export interface BalanceCajaFisicaSesion {
  montoEsperado: number;
  totalEfectivoVentas: number;
  totalRetiros: number;
  totalIngresos: number;
  totalIngresosEfectivo: number;
  totalIngresosDigital: number;
}

function movimientosActivosSesion(
  movimientos: MovimientoCaja[],
  sesionId: string,
): MovimientoCaja[] {
  return movimientos.filter(
    (m) => m.sesionId === sesionId && m.estado !== "anulado",
  );
}

/**
 * Efectivo esperado en gaveta (misma fórmula que MSW GET /estado y POST /cerrar).
 */
export function calcBalanceCajaFisicaSesion(
  montoInicial: number,
  ventas: Venta[],
  sesionId: string,
  movimientos: MovimientoCaja[],
): BalanceCajaFisicaSesion {
  const resumen = calcResumenVentasSesion(ventas, sesionId);
  const activas = ventasActivasSesion(ventas, sesionId);
  const movs = movimientosActivosSesion(movimientos, sesionId);
  const movOtros = movs.filter((m) => !isMovimientoAbonoCredito(m));
  const movCreditoAbonos = movs.filter(isMovimientoAbonoCredito);
  const egresos = splitEgresosCajaFisica(movs);
  const propina = sumPropinaPorMetodo(activas);
  const subtotalPorMetodo = sumSubtotalPorMetodo(activas);

  const totalIngresosManualesEfectivo = movOtros
    .filter(
      (m) =>
        m.tipo === "ingreso" && (m.metodoPago === "efectivo" || !m.metodoPago),
    )
    .reduce((acc, m) => acc + m.monto, 0);

  const totalIngresosManualesDigital = movOtros
    .filter(
      (m) =>
        m.tipo === "ingreso" &&
        m.metodoPago &&
        isDigitalIngresoSlot(m.metodoPago),
    )
    .reduce((acc, m) => acc + m.monto, 0);

  const totalAbonosCreditoEfectivo = movCreditoAbonos
    .filter((m) => m.metodoPago === "efectivo" || !m.metodoPago)
    .reduce((acc, m) => acc + m.monto, 0);

  const totalDevolucionesEfectivo = movs
    .filter(
      (m) =>
        m.tipo === "devolucion" &&
        (m.metodoPago === "efectivo" || !m.metodoPago),
    )
    .reduce((acc, m) => acc + m.monto, 0);

  const totalRetiros =
    egresos.retirosTop +
    egresos.retirosOtros +
    egresos.gastos +
    totalDevolucionesEfectivo;

  const totalIngresosEfectivo =
    totalIngresosManualesEfectivo + totalAbonosCreditoEfectivo;
  const totalIngresosDigital =
    totalIngresosManualesDigital +
    movCreditoAbonos
      .filter((m) => m.metodoPago && m.metodoPago !== "efectivo")
      .reduce((acc, m) => acc + m.monto, 0);

  const totalIngresos = totalIngresosEfectivo + totalIngresosDigital;
  const totalEfectivoVentas = resumen.efectivo;

  const { montoEsperado } = calcEsperadoGaveta({
    montoInicial,
    ventasEfectivo: Math.max(0, subtotalPorMetodo.efectivo - propina.efectivo),
    ingresosEfectivo: totalIngresosManualesEfectivo,
    abonosCreditoEfectivo: totalAbonosCreditoEfectivo,
    propinasEfectivo: propina.efectivo,
    retiros: egresos.retirosTop + egresos.retirosOtros,
    gastos: egresos.gastos,
    devolucionesEfectivo: totalDevolucionesEfectivo,
  });

  return {
    montoEsperado,
    totalEfectivoVentas,
    totalRetiros,
    totalIngresos,
    totalIngresosEfectivo,
    totalIngresosDigital,
  };
}
